import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { observer } from '@legendapp/state/react';
import { useTranslation } from 'next-i18next';
import { CartEmpty, Link, Text, TextIcon, getColorVar } from '@kidslabs/mario-nextjs-react-ui-core';
import { usePrice } from '@kidslabs/module-catalog';
import { convertPathToImageUrl } from '@kidslabs/module-core';
import { useCartSummary } from '../../hooks/use-cart-summary';
import type { MiniCartPopoverContentProps } from './mini-cart-popover-content.types';
import { useHybridCartPrices } from '../../hooks';
import { MiniCartButton } from '../mini-cart-button';
import { MiniCartItemsRenderer as DefaultMiniCartItemsRenderer } from '../mini-cart-items';

import styles from './mini-cart-popover-content.module.css';

export const MiniCartPopoverContent: React.FC<MiniCartPopoverContentProps> = observer(
    ({ fadeInOut, routes, MiniCartItemsRendererComponent = DefaultMiniCartItemsRenderer }) => {
        const { t } = useTranslation('common');

        const { totalItems } = useCartSummary();
        const { prices } = useHybridCartPrices();

        const totalTitle = useMemo(
            () => t('text-product-count', { count: totalItems, defaultValue: '(có {{count}} sản phẩm)' }),
            [totalItems, t]
        );

        const { price } = usePrice({
            amount: prices.subtotal_excluding_tax?.value ?? 0,
        });

        const imageProps = useMemo(
            () => ({
                src: convertPathToImageUrl('media/empty-mobile.png'),
                alt: t('text-cart-empty'),
                className: styles.emptyImage,
            }),
            [t]
        );

        const descriptionContent = useMemo(() => t('text-no-products'), [t]);

        const descriptionProps = useMemo(
            () => ({
                size: 'small' as const,
                className: styles.emptyDescription,
            }),
            []
        );

        const renderMiniCartButton = () => {
            return (
                <MiniCartButton
                    iconColor={getColorVar('primary', '0')}
                    className={styles.miniCartButton}
                    labelProps={{
                        className: styles.miniCartButtonLabel,
                    }}
                />
            );
        };

        const cartContent = useMemo(() => {
            if (totalItems > 0) {
                return <MiniCartItemsRendererComponent />;
            }

            return (
                <motion.div
                    initial="from"
                    animate="to"
                    exit="from"
                    variants={fadeInOut(0.25)}
                    className={styles.emptyContainer}
                >
                    <CartEmpty
                        containerClassName={styles.emptyCart}
                        imageProps={imageProps}
                        descriptionContent={descriptionContent}
                        descriptionProps={descriptionProps}
                    />
                </motion.div>
            );
        }, [totalItems, fadeInOut, imageProps, descriptionContent, descriptionProps, MiniCartItemsRendererComponent]);

        return (
            <div className={styles.wrapper}>
                <TextIcon
                    containerClassName={styles.textIconContainer}
                    leftIcon={renderMiniCartButton()}
                    text={t('text-mini-cart', 'Giỏ hàng')}
                    textClassName={styles.textIconText}
                    gap={0}
                />

                {/* Popover visible for ALL users — no authentication required for cart viewing */}
                <div className={styles.popover}>
                    <div className={styles.container}>
                        <div className={styles.arrow} />

                        <div className={styles.header}>
                            <div className={styles.headerTitle}>
                                <Text className={styles.title}>{t('text-mini-cart', 'Giỏ hàng')}</Text>

                                <Text className={styles.productCount}>{totalTitle}</Text>
                            </div>

                            <Text showIf={!!price} className={styles.price}>
                                {price}
                            </Text>
                        </div>

                        <div className={styles.itemsWrapper}>{cartContent}</div>

                        <div className={styles.footer}>
                            <div className={styles.footerContent}>
                                <Link href={routes.cart} className={styles.cartButton}>
                                    <Text className={styles.cartButtonText}>{t('text-view-cart')}</Text>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

export default MiniCartPopoverContent;
